/**
 * Aviator — the SERVER table clock (Phase D, plan §5.3/§6.1; the Andar Bahar Phase-C clock,
 * `../andar_bahar/serverTableClock.ts`, is the worked pattern this file follows).
 *
 * Same seam as `LocalTableClock`, wired to the real table: the round comes from
 * `GET /api/aviator/state`, the crash multiplier comes from `GET /api/aviator/flight`, and
 * every rupee on screen is one the server told us about. `Page.tsx` swaps ONE import line.
 *
 * ── THE FIVE RULES THIS FILE OBEYS ────────────────────────────────────────────────────────
 * 1. THE CRASH POINT IS NEVER PREDICTED. There is no `sampleCrash` here, no local RNG, no
 *    `secretM`. M arrives from the server — the `/flight` long-poll (whose predicate MySQL
 *    evaluates against `NOW(3)`), or `/state.result` once the round reaches `settling`. Until
 *    then the round is `flying` and the curve the Page draws is ANIMATION: it is a pure
 *    function of `takeoffAt` and the clock, it decides nothing, and it pays nobody.
 * 2. THE CLIENT NEVER PAYS ITSELF. `/bet`, `/unbet`, `/cashout` and `/state` all return the
 *    authoritative balance; the practice `Wallet` the Page owns is DRIVEN to that number and
 *    `setWalletBalance()` keeps the rest of the app in step. The only local arithmetic is the
 *    optimistic hold between a tap and its ack.
 * 3. AUTO CASH OUT IS THE SERVER'S. The client sends `autoCashout` with the bet and reflects
 *    what the server did. When the round's M is known and `autoX < M`, the panel shows the
 *    cash out as taken at `floor(stake × autoX)` — that is not a guess, it is the exact
 *    statement `worker.js runAutoCashouts` runs (`FLOOR(amount * auto_cashout)`) on inputs the
 *    server has already published. It is the same "provisional from the server's own decided
 *    result" move Andar Bahar makes between the deal and the credit.
 * 4. Time is the server's. Round stamps arrive as server epoch ms and are converted ONCE into
 *    the `performance.now()` domain the shipped Page subtracts from (`toPerf`), so
 *    `serverNow()` can keep returning `performance.now()` and no Page line changes.
 * 5. A tap is optimistic but never a lie. The chip appears immediately with `pending:true`;
 *    the ack replaces it with the real bet id and the real balance; a refusal removes it and
 *    puts the money back. A CASH OUT, alone among the actions, does NOT return an optimistic
 *    number: what it pays is decided by the server clock at the instant the request lands, so
 *    `cashout()` resolves with the SERVER's multiplier and the SERVER's rupees or with nothing
 *    at all. That is why it is the one method on this seam that returns a Promise.
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT READ (2026-09-06) ────────────────────────────────
 * `/state.myBets[].payout`. `bg-tables-worker` writes every bet's payout inside the DECISION
 * transaction — while the round is still `locked`, a second before takeoff — and `getState`
 * publishes that column with no reveal gate. For a bet carrying an AUTO CASH OUT, a non-zero
 * `payout` therefore says `autoX < M` BEFORE the plane has left the ground. This clock never
 * looks at it: a cashed-out bet's rupees are recomputed here from `cashoutMult` (identical
 * arithmetic), and the settled round's figures are taken from `lastResult.myOutcome`, which
 * only exists once the result is public. The leak is a SERVER defect and is reported, not
 * fixed here — but nothing this client does can be built on top of it.
 *
 * ── WHY THE DISPLAYED ROUND IS NOT ALWAYS `state.round` ───────────────────────────────────
 * The server's `round` is the oldest NON-TERMINAL row: the moment round N settles it stops
 * being `round` and becomes `lastResult`, while round N+1 (inserted `pending` at decision
 * time) becomes `round`. The player, meanwhile, is still looking at N's crash. So the clock
 * displays "the newest round whose window has actually opened", which is N until N+1 opens.
 */

import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import {
  mAt, tFor, q2, ARM_MS, SETTLE_MS, INTERMISSION_MS, WINDOW_MS, MAX_MULT,
  type PanelState,
} from './engine';
import { mix } from './prng';
import { createClockSync, type ClockSync } from '../_shared/serverClock';
import { createPoller, type Poller } from '../_shared/poller';
import {
  LocalTableClock,
  type TableClock, type Round, type RoundStatus, type Panel, type Wallet, type CashoutResult,
} from './tableClock';

// Re-exported so `Page.tsx`'s single import line can name everything it used to import from
// `./tableClock` plus this implementation.
export { LocalTableClock } from './tableClock';
export type { TableClock, Round, RoundStatus, Panel, Wallet, CashoutResult } from './tableClock';

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Wire types — exactly what `_tables/model.js` serialises (its whitelist, not a DB row)
 * ════════════════════════════════════════════════════════════════════════════════════════ */

type SrvStatus = 'pending' | 'betting' | 'locked' | 'resolving' | 'settling' | 'settled' | 'void';

/** `aviator/adapter.js resultView` — a whitelist of exactly one number, and model.js only
 *  calls it when the status is `settling` or `settled` (REVEAL_STATUSES.aviator). */
type SrvResult = { M: number } | null;

type SrvOutcome = {
  staked: number; returned: number; net: number;
  perBet?: { betId: number; market: string; amount: number; payout: number; cancelled: boolean }[];
} | null;

type SrvRound = {
  id: number; no: number; tableId: number; status: SrvStatus;
  opensAt: number | null; locksAt: number | null; resolvesAt: number | null;
  /** `settlesAt`/`nextOpensAt` are NULL for aviator until the flight is over: publishing them
   *  during a flight would hand out `crash_at = settlesAt − 1500` (model.js roundView). */
  settlesAt: number | null; nextOpensAt: number | null; settledAt: number | null;
  result: SrvResult;
  pot?: number; bettors?: number;
  myOutcome?: SrvOutcome;
};

type SrvMyBet = {
  betId: number; roundId: number; roundNo: number; market: string; selection: unknown;
  amount: number; autoCashout: number | null;
  cashedOut: boolean; cashoutMult: number | null;
  /** DELIBERATELY UNUSED — see the header. Declared so the shape is documented, not read. */
  payout: number;
};

type AvState = {
  serverNow: number;
  wallet: number;
  enabled: boolean;
  bet: { min: number; max: number; presets: number[]; maxPerRound: number; maxAutoCashout?: number };
  table: {
    id: number; name: string; pace: string; windowMs: number; intermissionMs: number;
    status: string; fakeSeed: number; sortOrder: number;
  };
  round: SrvRound | null;
  nextRound: SrvRound | null;
  lastResult: SrvRound | null;
  history: SrvRound[];
  myBets: SrvMyBet[];
};

type BetAck = {
  betId: number; roundId: number; roundNo: number; placedOn: number; tableId: number;
  market: string; amount: number; autoCashout: number | null;
  roundTotal: number; wallet: number; duplicate?: boolean; cancelled?: boolean;
};
type UnbetAck = { betId: number; roundId: number; roundNo: number; refunded: number; wallet: number; duplicate?: boolean };
type CashoutAck = {
  betId: number; roundId: number; tableId: number;
  multiplier: number | null; payout: number; wallet: number; duplicate?: boolean;
};
/** `controller.js flight` — `m` is null until the DB clock passes `crash_at`. */
type FlightAck = {
  roundId: number; status: SrvStatus; crashed: boolean; m: number | null;
  takeoffAt: number | null; serverNow: number;
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Constants that MUST agree with the server
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** `paces.js` PACES.aviator.lock — `/bet` and `/unbet` refuse inside this cushion before
 *  `locks_at`, so the client greys the same instant rather than a tick later. */
const LOCK_MS = 500;
/** The two bet panels ARE the two markets (`aviator/adapter.js` MARKETS). */
const MARKETS = ['p0', 'p1'] as const;
type Market = (typeof MARKETS)[number];
/** The local re-derive tick. The Page re-renders only when something it shows changed. */
const TICK_MS = 100;
/** Start the `/flight` long-poll at the lock, so an instant crash (M = 1.00, 3% of rounds,
 *  `crash_at === resolves_at`) is known the moment the plane would have left the ground. */
const FLIGHT_RETRY_MS = 900;
/** The curve tops out at X500 ≈ 56.5 s. Past that plus a beat, a flight with no reveal is a
 *  sync problem, not a very long flight. */
const MAX_FLIGHT_MS = Math.ceil(tFor(MAX_MULT) * 1000) + 2_000;
/** `/bet` with `allowNext` needs round N+1 to EXIST, and the worker only inserts it inside the
 *  decision transaction — so a tap in the ~800 ms between the lock cushion and the decision is
 *  refused `round_locked` through no fault of the player. Retry it, with the same idempotency
 *  key, rather than snatching the chip back off the table. */
const BET_RETRY_MS = 600;
const BET_RETRIES = 2;

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Pure helpers
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** `?practice=1` → the Phase-A local clock (dev sandbox), never the money path. */
function practiceRequested(): boolean {
  try { return new URLSearchParams(window.location.search).get('practice') === '1'; }
  catch { return false; }
}

/** True when this page is running on the practice clock rather than the real table. The
 *  ribbon badge reads from it, so a real-money screen can never say PRACTICE. */
export const isPracticeTable = practiceRequested;

const uuid = (): string => {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* older webview */ }
  return `av-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The rupees a cash out at `m` returns — `engine.payout`, which is the same
 *  `floor(stake × m)` both the manual (`model.cashout`) and the auto
 *  (`worker.runAutoCashouts`) server paths compute. */
const rupeesAt = (stake: number, m: number) => Math.floor(stake * m + 1e-9);

const marketOf = (p: Panel): Market => MARKETS[p];
const betKey = (roundNo: number, market: Market) => `${roundNo}:${market}`;

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The clock
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** One chip on one panel of one round, plus what the server needs to move it. */
type LiveBet = {
  /** the idempotency key this chip was bought with — one per tap, forever */
  key: string;
  /** the real `shared_round_bets.id`, null while the tap is in flight */
  betId: number | null;
  /** false once the ack has landed */
  pending: boolean;
  roundNo: number;
  roundId: number | null;
  market: Market;
  stake: number;
  autoX: number | null;
  /** set ONLY from the server: a `/cashout` ack, `myBets.cashoutMult`, or `autoX < M` on a
   *  crash the server has already revealed. Never from the local animation. */
  cashed: { m: number; won: number } | null;
};

export class ServerTableClock implements TableClock {
  private clock: ClockSync;
  private poller: Poller;
  private timer = 0;
  private subs = new Set<() => void>();
  private dead = false;

  /** The last `/state` we believe. Null until the first poll lands. */
  private state: AvState | null = null;
  private tableId: number | null = null;

  /** My chips, keyed `roundNo:market`. Kept past the settle so the crash screen can still say
   *  what I staked (the server drops settled rounds out of `myBets`). */
  private bets = new Map<string, LiveBet>();
  /** Chips whose place-ack has not landed but which the player has already taken back. */
  private cancelOnAck = new Set<string>();
  /** betId → idempotency key, for every chip whose `/unbet` is still in flight. A `/state`
   *  read BEFORE the refund committed must not put the chip back on the table. */
  private cancelling = new Map<number, string>();
  /** Bet keys with a `/cashout` in flight — a second tap must not celebrate twice. */
  private cashing = new Set<string>();

  /** What the server has revealed about each round's flight. */
  private flightM = new Map<number, number>();       // roundId → M (2 dp, the server's)
  private flightTakeoff = new Map<number, number>(); // roundId → resolves_at (server epoch ms)
  private flightNone = new Set<number>();            // roundId → terminal with no flight at all
  private flightBusy = false;
  private flightNextAt = 0;                          // perf ms; back-off after a failed probe

  /** The server's balance, and the optimistic holds not yet reflected in it. */
  private serverWallet = 0;
  private walletKnown = false;
  private holds = new Map<string, number>();
  /** Bumped on every acked write, so a `/state` that was already in flight cannot rewind it. */
  private writeSeq = 0;

  private r: Round | null = null;
  private sig = '';
  private displayedNo: number | null = null;
  private displayedId: number | null = null;
  private syncingFlag = false;
  private lastErr: string | null = null;

  /**
   * @param tableSeed  the Phase-A local seed. Ignored on a real table: the fake crowd must be
   *                   a pure function of the SERVER's `(table_id, round_no, fake_seed)` so two
   *                   players at one table see one theatre (plan §5.4). Kept in the signature
   *                   so `Page.tsx` needs no change beyond its import.
   * @param wallet     the Page's balance sink — driven to the server's number, never trusted.
   */
  constructor(private tableSeed: number, private wallet: Wallet, _firstRoundNo = 1) {
    // Practice mode keeps the Phase-A clock alive for the dev sandbox (`?practice=1`): a
    // constructor may hand back another object, so the swap costs the Page nothing.
    if (practiceRequested()) {
      return new LocalTableClock(tableSeed, wallet, _firstRoundNo) as unknown as ServerTableClock;
    }

    this.clock = createClockSync('/aviator/time');
    this.clock.startAutoSync(30_000);

    this.poller = createPoller({
      poll: () => this.pollState(),
      now: () => performance.now(),
      edges: () => {
        const r = this.r;
        if (!r) return [];
        return [r.locksAt, r.takeoffAt, r.crashAt, r.settlesAt, r.nextOpensAt];
      },
      busy: () => this.holds.size > 0 || this.cashing.size > 0,
      onError: (e) => { if (e instanceof ApiError) this.lastErr = e.message; },
    });
    this.poller.start();

    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /* ── the TableClock interface ────────────────────────────────────────────────────────── */

  /** The Page renders in the `performance.now()` domain; every server stamp was converted on
   *  the way in (`toPerf`), so this stays exactly what `LocalTableClock` returned. */
  serverNow(): number { return performance.now(); }

  /**
   * The round in play.
   *
   * Null until the first `/state` lands. `Page.tsx` guards it (`round?.status`, `if (!clock ||
   * !round) return`); a placeholder round would mean a fabricated takeoff time on screen and a
   * countdown counting down to nothing, so we return nothing rather than something untrue.
   */
  round(): Round {
    this.recompute();
    return this.r as Round;
  }

  /**
   * The live multiplier. Pure animation between takeoff and the server's reveal: it is
   * `mAt(elapsed)` on the SERVER's takeoff stamp, it never approaches a number this client was
   * told, and the instant the crash lands the status leaves `flying` and this returns to 1.
   */
  mNow(): number {
    this.recompute();
    const r = this.r;
    if (!r || r.status !== 'flying') return 1;
    return mAt((performance.now() - r.takeoffAt) / 1000);
  }

  panel(p: Panel): PanelState {
    this.recompute();
    return this.panelStateFor(p);
  }

  /** Crash points, newest first — the server's, from settled rounds. The round on screen is
   *  prepended the moment IT crashes, because it does not reach `/state.history` until it
   *  settles 1.5 s later and the player must see their own round land first. */
  history(): number[] {
    const rows = this.state?.history ?? [];
    const out: { no: number; M: number }[] = [];
    for (const h of rows) {
      const M = num(h?.result?.M);
      if (M == null) continue;                       // an idle round never flew: no crash point
      out.push({ no: Number(h.no), M: q2(M) });
    }
    const r = this.r;
    if (r && r.M != null && (r.status === 'crashed' || r.status === 'intermission')
        && !out.some((x) => x.no === r.no)) {
      out.unshift({ no: r.no, M: r.M });
    }
    return out.slice(0, 20).map((x) => x.M);
  }

  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }

  /** The server has told us the balance at least once (first `/state`, or a write ack). */
  walletReady() { return this.walletKnown; }

  /**
   * Put a chip on a panel. Synchronous, like the local clock: the answer is the LOCAL guard's
   * (panel busy / window closed / not enough money); the server's answer arrives on the ack
   * and either confirms the chip or takes it back off the table.
   *
   * `queued` is a PREDICTION made with the server's own cushion (`locks_at − lock_ms`), and
   * the ack corrects the chip's round if the prediction was wrong.
   */
  bet(p: Panel, stake: number, autoX: number | null): { ok: true; queued: boolean } | { ok: false; reason: string } {
    this.recompute();
    const r = this.r;
    if (!r || !this.state) return { ok: false as const, reason: 'sync' };
    if (!this.walletKnown) return { ok: false as const, reason: 'wallet' };

    const mkt = marketOf(p);
    // One live chip per panel. A chip on a round that has already crashed is not live — the
    // local clock lets a `cashed`/`lost` panel take a new bet straight away, and so does this.
    const over = r.status === 'crashed' || r.status === 'intermission';
    if ((this.bets.has(betKey(r.no, mkt)) && !over) || this.bets.has(betKey(r.no + 1, mkt))) {
      return { ok: false as const, reason: 'busy' };
    }

    const amt = Math.floor(Number(stake));
    if (!(amt > 0)) return { ok: false as const, reason: 'balance' };
    if (amt > this.displayBalance()) return { ok: false as const, reason: 'balance' };

    // The chip is AIMED at the round in play; `allowNext` is what moves it to N+1 server-side,
    // and the ack says where it actually landed. N+1 may not exist yet (the worker inserts it
    // inside the decision transaction) — `sendBet` retries that refusal rather than snatching
    // the chip back off the table.
    const queued = !this.roundBettable(r.no);
    const landsOn = queued ? r.no + 1 : r.no;

    const key = uuid();
    const b: LiveBet = {
      key, betId: null, pending: true,
      roundNo: landsOn,
      roundId: queued ? this.srvRoundByNo(landsOn)?.id ?? null : this.displayedId,
      market: mkt, stake: amt,
      autoX: autoX == null ? null : q2(Number(autoX)),
      cashed: null,
    };
    this.bets.set(betKey(b.roundNo, mkt), b);
    this.holds.set(key, -amt);                       // optimistic: the chip has left the wallet
    this.pushWallet();
    this.emit();
    void this.sendBet(b, r.no);
    return { ok: true as const, queued };
  }

  /** Take this panel's chip back. Returns whether there was one to take. */
  cancel(p: Panel): boolean {
    this.recompute();
    const r = this.r;
    if (!r) return false;
    const mkt = marketOf(p);
    for (const no of [r.no, r.no + 1]) {
      const k = betKey(no, mkt);
      const b = this.bets.get(k);
      if (!b || b.cashed) continue;
      // `/unbet` is refused once the round is past `locks_at − lock_ms`; refusing here as well
      // means the chip never disappears off the table only to be put back by the refusal.
      if (!this.roundBettable(no)) continue;
      this.bets.delete(k);
      if (b.betId != null) {
        this.holds.set(`u:${b.key}`, b.stake);       // optimistic: the chip is on its way back
        this.cancelling.set(b.betId, b.key);
        void this.sendUnbet(b);
      } else {
        // The place-ack has not landed, so there is nothing yet to refund. Dropping the chip's
        // own hold (rather than adding a second, opposite one) puts the money back on screen
        // and leaves the two possible futures consistent: if the bet never landed the balance
        // is already right, and if it did, the ack sets the SERVER's post-debit balance and
        // opens the refund hold in the same breath. Two live holds for one chip would also
        // never be cleared if the retry below is abandoned.
        this.holds.delete(b.key);
        this.cancelOnAck.add(b.key);                 // cancel it the moment we learn its id
      }
      this.pushWallet();
      this.emit();
      return true;
    }
    return false;
  }

  /**
   * Take the money mid-flight.
   *
   * The ONLY authority on what this pays is `model.cashout`, which prices the multiplier from
   * the DB clock at the instant the request lands and refuses outright once `NOW(3)` has passed
   * `crash_at`. So this resolves with the server's `multiplier` and the server's `payout`, or
   * with a refusal — never with a number composed here. That is the whole reason this method
   * returns a Promise while `bet()` and `cancel()` do not: a stake can be shown optimistically
   * and put back if refused, a WIN cannot.
   */
  async cashout(p: Panel): Promise<CashoutResult> {
    this.recompute();
    const r = this.r;
    if (!r) return { ok: false as const, reason: 'not_flying' as const };
    const b = this.bets.get(betKey(r.no, marketOf(p)));
    if (!b || b.betId == null || b.cashed) return { ok: false as const, reason: 'not_flying' as const };
    if (this.cashing.has(b.key)) return { ok: false as const, reason: 'not_flying' as const };

    this.cashing.add(b.key);
    try {
      const ack = await api.post<CashoutAck>('/aviator/cashout', { betId: b.betId });
      if (this.dead) return { ok: false as const, reason: 'not_flying' as const };
      this.writeSeq += 1;
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;
      const m = num(ack.multiplier);
      const won = Math.max(0, Math.floor(Number(ack.payout) || 0));
      // A replay of a bet whose `cashout_mult` is somehow unreadable still carries the rupees
      // the server paid, so show those and read the X back out of them.
      const cashed = { m: m == null ? (b.stake > 0 ? q2(won / b.stake) : 1) : q2(m), won };
      this.markCashed(Number(ack.betId), cashed);
      this.pushWallet();
      this.emit();
      this.poller.kick();
      return { ok: true as const, m: cashed.m, won: cashed.won };
    } catch (e) {
      if (this.dead) return { ok: false as const, reason: 'not_flying' as const };
      const code = e instanceof ApiError ? e.code : '';
      const message = e instanceof ApiError ? e.message : 'That cash out did not reach us.';
      this.lastErr = message;
      this.emit();
      this.poller.kick();
      if (code === 'too_late') return { ok: false as const, reason: 'too_late' as const };
      return { ok: false as const, reason: 'not_flying' as const, message };
    } finally {
      this.cashing.delete(b.key);
    }
  }

  destroy() {
    this.dead = true;
    window.clearInterval(this.timer);
    try { this.poller?.stop(); } catch { /* already stopped */ }
    try { this.clock?.destroy(); } catch { /* already stopped */ }
    this.subs.clear();
    // The game owned the balance while it was on screen; hand the rest of the app the truth.
    void refreshWallet();
  }

  /* ── extras (not part of the interface; safe for a later UI to read) ─────────────────── */

  /** True only when the table is genuinely out of sync: still `flying` past the longest crash
   *  point the curve can reach (X500 ≈ 56.5 s) with no reveal from either `/flight` or
   *  `/state`. A round that never flew at all is NOT this — that is a documented idle table. */
  syncing(): boolean { return this.syncingFlag; }
  /** The last error message the server gave us (bet refused, cash out too late, limit hit). */
  lastError(): string | null { return this.lastErr; }
  /** The server's bet bounds for this game — the Page's hardcoded caps should come from here. */
  limits(): AvState['bet'] | null { return this.state?.bet ?? null; }
  /** The live table (id, name, pace) once known. */
  table(): AvState['table'] | null { return this.state?.table ?? null; }
  /** Server epoch ms, for anything that needs the wall clock rather than the perf domain. */
  serverEpochNow(): number { return this.clock.serverNow(); }
  isSynced(): boolean { return this.clock.isSynced(); }

  /* ── polling ─────────────────────────────────────────────────────────────────────────── */

  private async pollState(): Promise<void> {
    const seqAtStart = this.writeSeq;
    const t0 = performance.now();
    const path = this.tableId == null ? '/aviator/state' : `/aviator/state?table=${this.tableId}`;
    const st = await api.get<AvState>(path);
    const t1 = performance.now();
    if (this.dead) return;

    // A server timestamp we can anchor on before the first `/time` burst completes.
    this.clock.noteFallback(Number(st.serverNow), t0, t1);

    this.state = st;
    this.tableId = Number(st.table?.id) || this.tableId;
    this.lastErr = null;

    // A poll is only fresher than the last write if no write acked while it was in flight:
    // the ack carries the POST-write balance and bet list, this read may predate both. When a
    // write did land, keep what the ack told us and go round again immediately.
    if (seqAtStart === this.writeSeq) {
      this.serverWallet = Number(st.wallet) || 0;
      this.walletKnown = true;
      this.mergeServerBets(st);
    } else {
      this.poller.kick();
    }
    this.pushWallet();
    this.tick();                                     // re-derive + emit on the fresh truth
  }

  /**
   * Reconcile my chips with the server's. The server is authoritative for every round it still
   * reports (`round` + `nextRound`): a chip it does not list was cancelled or never landed.
   * Chips still pending (no ack yet) are kept — the server has not seen them.
   */
  private mergeServerBets(st: AvState) {
    const liveNos: number[] = [];
    if (st.round) liveNos.push(Number(st.round.no));
    if (st.nextRound) liveNos.push(Number(st.nextRound.no));

    const fromServer = new Map<string, SrvMyBet>();
    for (const mb of st.myBets || []) {
      const mkt = mb.market as Market;
      if (mkt !== 'p0' && mkt !== 'p1') continue;
      // A chip whose refund is in flight must not reappear because this read predates it.
      if (this.cancelling.has(Number(mb.betId))) continue;
      fromServer.set(betKey(Number(mb.roundNo), mkt), mb);
    }

    for (const no of liveNos) {
      for (const mkt of MARKETS) {
        const k = betKey(no, mkt);
        const srv = fromServer.get(k);
        const mine = this.bets.get(k);
        if (srv) {
          const stake = Number(srv.amount);
          const cashed = srv.cashedOut && num(srv.cashoutMult) != null
            ? { m: q2(Number(srv.cashoutMult)), won: rupeesAt(stake, Number(srv.cashoutMult)) }
            : (mine && mine.betId === Number(srv.betId) ? mine.cashed : null);
          this.bets.set(k, {
            key: mine && mine.betId === Number(srv.betId) ? mine.key : `srv:${srv.betId}`,
            betId: Number(srv.betId), pending: false,
            roundNo: Number(srv.roundNo), roundId: Number(srv.roundId),
            market: mkt, stake,
            autoX: num(srv.autoCashout),
            cashed,
          });
        } else if (mine && !(mine.pending && !this.cancelOnAck.has(mine.key))) {
          this.bets.delete(k);
        }
      }
    }

    // A settled round leaves `myBets`, so the chip that is still on screen keeps whatever the
    // last live read said — except its OUTCOME, which the public per-bet list now carries.
    const lr = st.lastResult;
    const perBet = lr?.myOutcome?.perBet;
    if (lr && Array.isArray(perBet)) {
      for (const pb of perBet) {
        const mkt = pb.market as Market;
        if (mkt !== 'p0' && mkt !== 'p1') continue;
        const b = this.bets.get(betKey(Number(lr.no), mkt));
        if (!b || b.betId !== Number(pb.betId)) continue;
        if (pb.cancelled) { this.bets.delete(betKey(Number(lr.no), mkt)); continue; }
        const paid = Math.max(0, Math.floor(Number(pb.payout) || 0));
        if (paid > 0 && !b.cashed) {
          // The round is public now, so this figure carries no secret. The X is the one the
          // player set WHEN the rupees match the auto order exactly (the worker stamps
          // `cashout_mult = auto_cashout` and pays `FLOOR(amount × auto_cashout)`); otherwise
          // this bet was cashed out by hand in a session we did not see, so the X is read back
          // out of the payout rather than asserted from an order that did not pay it.
          const byAuto = b.autoX != null && paid === rupeesAt(b.stake, b.autoX);
          b.cashed = {
            m: byAuto ? (b.autoX as number) : (b.stake > 0 ? q2(paid / b.stake) : 1),
            won: paid,
          };
        }
      }
    }

    // Forget rounds that are neither live nor on screen (a long session must not grow).
    const keep = new Set<number>([...liveNos]);
    if (this.displayedNo != null) keep.add(this.displayedNo);
    if (st.lastResult) keep.add(Number(st.lastResult.no));
    for (const k of [...this.bets.keys()]) {
      const no = Number(k.split(':')[0]);
      if (!keep.has(no)) this.bets.delete(k);
    }
  }

  /* ── the flight long-poll ────────────────────────────────────────────────────────────── */

  /**
   * `GET /flight?round=` hangs up to 3 s and answers the moment the DB clock passes
   * `crash_at`. It is the ONLY fast path to M: `/state` does not reveal the result until the
   * round reaches `settling`, 1.5 s after the crash. Nothing here predicts anything — if the
   * poll fails the plane simply keeps climbing until `/state` reveals the number.
   */
  private maybeProbeFlight() {
    if (this.dead || this.flightBusy) return;
    // A backgrounded tab is not watching a flight, and every probe holds a server connection
    // for up to 3 s and spends the per-player limiter. The `/state` poller pauses on hidden for
    // the same reason (`poller.ts` "PAUSE ON HIDDEN"); the first tick back in front resumes.
    if (typeof document !== 'undefined' && document.hidden) return;
    const st = this.state;
    const r = this.r;
    if (!st || !r) return;
    const id = this.displayedId;
    if (id == null) return;
    if (this.flightM.has(id) || this.flightNone.has(id)) return;
    const src = this.srvRoundByNo(r.no);
    if (!src) return;
    if (src.result && num(src.result.M) != null) return;    // already revealed by /state
    if (src.status === 'settled' || src.status === 'settling') {
      // Terminal and still no result: `/state` reveals for both of those statuses, so there is
      // nothing left to reveal — this round never flew (the §5.2 idle short-circuit writes
      // rounds BORN settled on a table nobody has bet on for 15 minutes). No probe can add to
      // that, and spending a limiter slot to be told so again is waste.
      this.flightNone.add(id);
      this.prune();
      return;
    }
    // Nothing to learn before the betting window closes, and probing then would only spend the
    // per-player limiter.
    if (performance.now() < r.locksAt) return;
    if (performance.now() < this.flightNextAt) return;
    void this.probeFlight(id);
  }

  private async probeFlight(roundId: number) {
    this.flightBusy = true;
    try {
      const j = await api.get<FlightAck>(`/aviator/flight?round=${roundId}`);
      if (this.dead) return;
      const takeoff = num(j.takeoffAt);
      if (takeoff != null) this.flightTakeoff.set(roundId, takeoff);
      const m = num(j.m);
      if (j.crashed === true && m != null) {
        this.flightM.set(roundId, q2(m));
        this.prune();
      } else if (j.status === 'void') {
        // §5.2 outage void: the stake is safe and the worker will hand it back. There is no
        // crash to draw, so the table simply waits for the next round.
        this.flightNone.add(roundId);
        this.lastErr = 'That round is under review — your stake is safe.';
        this.prune();
      } else if (j.status === 'settled' || j.status === 'settling') {
        // Terminal with no crash instant: the §5.2 idle short-circuit writes rounds BORN
        // settled on a table nobody has bet on for 15 minutes — no decision, no flight, no M.
        this.flightNone.add(roundId);
        this.prune();
      }
      this.flightNextAt = 0;
    } catch (e) {
      if (this.dead) return;
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 404) { this.flightNone.add(roundId); this.prune(); }
      else this.flightNextAt = performance.now() + FLIGHT_RETRY_MS;
      if (e instanceof ApiError && status !== 404) this.lastErr = e.message;
    } finally {
      this.flightBusy = false;
      if (!this.dead) this.tick();
    }
  }

  /** A long session must not grow: only the last few rounds can still be on screen. */
  private prune() {
    const cap = 8;
    for (const map of [this.flightM, this.flightTakeoff] as Map<number, number>[]) {
      while (map.size > cap) { const k = map.keys().next().value as number; map.delete(k); }
    }
    while (this.flightNone.size > cap) {
      const k = this.flightNone.values().next().value as number;
      this.flightNone.delete(k);
    }
  }

  /* ── writes ──────────────────────────────────────────────────────────────────────────── */

  private async sendBet(b: LiveBet, aimedAtNo: number, attempt = 0) {
    try {
      const ack = await api.post<BetAck>('/aviator/bet', {
        table: this.tableId ?? undefined,
        // The chip is aimed at the round IN PLAY; `allowNext` is the server-side move to N+1
        // (aviator is the only game whose adapter opts into it, plan §5.3), so the panel can
        // pre-bet after the lock instead of bouncing.
        roundNo: aimedAtNo,
        market: b.market,
        amount: b.stake,
        ...(b.autoX == null ? {} : { autoCashout: b.autoX }),
        idempotencyKey: b.key,
        allowNext: true,
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(b.key);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;

      const landed: LiveBet = {
        ...b,
        pending: false,
        betId: Number(ack.betId),
        roundNo: Number(ack.roundNo),
        roundId: Number(ack.roundId),
        stake: Number(ack.amount) || b.stake,
        autoX: num(ack.autoCashout),
      };
      // Where we optimistically drew the chip, and where the money actually is.
      this.bets.delete(betKey(b.roundNo, b.market));

      if (ack.cancelled === true) {
        // A replayed key whose bet was already taken back: there is no chip on this table.
        this.pushWallet();
        this.emit();
        this.poller.kick();
        return;
      }
      if (this.cancelOnAck.has(b.key)) {
        // CANCEL landed while this tap was in flight: take it straight back off the table.
        this.cancelOnAck.delete(b.key);
        this.holds.set(`u:${b.key}`, landed.stake);
        this.cancelling.set(landed.betId as number, b.key);
        void this.sendUnbet(landed);
      } else {
        this.bets.set(betKey(landed.roundNo, landed.market), landed);
      }
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      const code = e instanceof ApiError ? e.code : '';
      // The dead zone between the lock cushion and the worker's decision: round N is closed and
      // round N+1 does not exist yet, so `allowNext` has nothing to move the chip to. That is a
      // timing artefact, not a refusal of this player — retry the SAME idempotency key (which
      // can never double-debit) rather than snatching the chip back.
      if (code === 'round_locked' && attempt < BET_RETRIES && !this.cancelOnAck.has(b.key)) {
        window.setTimeout(() => {
          if (this.dead) return;
          if (this.bets.get(betKey(b.roundNo, b.market))?.key === b.key) {
            void this.sendBet(b, aimedAtNo, attempt + 1);
            return;
          }
          // The player took the chip back while we were waiting to retry. Nothing was ever
          // sent, so release its hold here — this is the one path out of `sendBet` that does
          // not end in an ack or a refusal, and a hold left behind would sit on the balance
          // and keep the poller in its fast cadence for the rest of the session.
          this.holds.delete(b.key);
          this.cancelOnAck.delete(b.key);
          this.pushWallet();
          this.emit();
        }, BET_RETRY_MS);
        return;
      }
      // Refused: the money never left. Remove the chip and hand the hold back.
      this.holds.delete(b.key);
      this.cancelOnAck.delete(b.key);
      const cur = this.bets.get(betKey(b.roundNo, b.market));
      if (cur && cur.key === b.key) this.bets.delete(betKey(b.roundNo, b.market));
      this.lastErr = e instanceof ApiError ? e.message : 'That bet did not go through.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  private async sendUnbet(b: LiveBet) {
    const holdKey = `u:${b.key}`;
    try {
      const ack = await api.post<UnbetAck>('/aviator/unbet', {
        betId: b.betId, idempotencyKey: `${b.key}:undo`, reason: 'cancel',
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(holdKey);
      if (b.betId != null) this.cancelling.delete(b.betId);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      // Too late (or any refusal): the chip is STILL on the table and still at risk. Put it
      // back on screen rather than showing money the player does not have.
      this.holds.delete(holdKey);
      if (b.betId != null) this.cancelling.delete(b.betId);
      const k = betKey(b.roundNo, b.market);
      if (!this.bets.has(k)) this.bets.set(k, { ...b, pending: false });
      this.lastErr = e instanceof ApiError ? e.message : 'That bet could not be cancelled.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  /**
   * Stamp a bet as cashed out, BY ID rather than by the object the caller was holding: a
   * `/state` that landed while the cash-out was in flight will have replaced the map entry, and
   * writing to the object it replaced would leave the panel showing FLYING over money the
   * server has already paid.
   */
  private markCashed(betId: number, cashed: { m: number; won: number }) {
    for (const b of this.bets.values()) {
      if (b.betId === betId) b.cashed = cashed;
    }
  }

  /* ── wallet ──────────────────────────────────────────────────────────────────────────── */

  private displayBalance(): number {
    let d = 0;
    for (const v of this.holds.values()) d += v;
    return Math.max(0, (this.walletKnown ? this.serverWallet : this.wallet.balance()) + d);
  }

  /** Drive the Page's balance sink to the number we believe, using only the `Wallet` seam. */
  private pushWallet() {
    if (!this.walletKnown) return;
    const target = this.displayBalance();
    const cur = this.wallet.balance();
    const d = target - cur;
    if (d > 0) this.wallet.credit(d);
    else if (d < 0) this.wallet.debit(-d);
    setWalletBalance(this.serverWallet);             // the app-wide chip shows the SERVER balance
  }

  /* ── derivation ──────────────────────────────────────────────────────────────────────── */

  private srvRoundByNo(no: number): SrvRound | null {
    const st = this.state;
    if (!st) return null;
    for (const c of [st.round, st.nextRound, st.lastResult]) {
      if (c && Number(c.no) === no) return c;
    }
    return null;
  }

  /**
   * Is this round still taking chips? EXACTLY `lockRoundShared`'s predicate: status
   * `betting` or `pending`, and `locks_at − lock_ms` still in the future. Anything looser
   * would light a button the server is about to refuse.
   */
  private roundBettable(no: number): boolean {
    const c = this.srvRoundByNo(no);
    if (!c) return false;
    if (c.status !== 'betting' && c.status !== 'pending') return false;
    const locks = c.locksAt == null ? null : this.clock.toPerf(Number(c.locksAt));
    return locks != null && performance.now() < locks - LOCK_MS;
  }

  /**
   * Choose the round on screen and derive its phase from the server's stamps.
   *
   * Everything here is a pure function of (`this.state`, what the server has revealed about
   * the flight, now). No timers advance it and no local randomness feeds it: if the server has
   * not revealed the crash, the phase does not leave `flying`.
   */
  private recompute(): string {
    const st = this.state;
    if (!st) { this.r = null; this.displayedNo = null; this.displayedId = null; return ''; }
    const now = performance.now();
    const toPerf = (ms: number | null | undefined) => (ms == null ? null : this.clock.toPerf(Number(ms)));

    // Candidates, newest first: the round after the one in play, the one in play, the one just
    // settled. A `void` round is not shown — there is nothing to show.
    const cands = [st.nextRound, st.round, st.lastResult]
      .filter((c): c is SrvRound => !!c && c.status !== 'void')
      .sort((a, b) => Number(b.no) - Number(a.no));
    if (!cands.length) { this.r = null; this.displayedNo = null; this.displayedId = null; return ''; }

    // The newest round whose window has actually opened; before the first one opens, the
    // earliest known round (so the countdown is on screen at boot).
    const src = cands.find((c) => {
      const o = toPerf(c.opensAt);
      return o != null && o <= now;
    }) || cands[cands.length - 1];

    const roundId = Number(src.id);
    const opensAt = toPerf(src.opensAt) ?? now;
    const locksAt = toPerf(src.locksAt) ?? (opensAt + Number(st.table?.windowMs || WINDOW_MS));
    // `resolves_at` is only stamped at the decision, so before the lock the takeoff is the
    // constant arm (`paces.js` PACES.aviator.arm) — a number that carries nothing about M.
    const takeoffAt = toPerf(src.resolvesAt) ?? (locksAt + ARM_MS);

    // ── M: ONLY from the server ────────────────────────────────────────────────────────
    // `/state.result` exists once the round reaches `settling` (model.js REVEAL_STATUSES);
    // before that the `/flight` long-poll is the only source, and it answers when the DB clock
    // passes `crash_at`. There is no third branch, and none of this is derived from the curve.
    let M: number | null = num(src.result?.M);
    if (M != null) M = q2(M);
    if (M == null && this.flightM.has(roundId)) M = this.flightM.get(roundId) as number;

    // The crash instant. `paces.timelineFor`: crash_at = resolves_at + round(t(M) × 1000), and
    // settles_at = crash_at + 1500. Once the round is public the server's own `settlesAt` is
    // preferred; before that the identity above reproduces it exactly.
    let crashAt: number | null = null;
    let settlesAt: number | null = toPerf(src.settlesAt);
    if (settlesAt != null) {
      crashAt = settlesAt - SETTLE_MS;
    } else if (M != null) {
      const takeoffSrv = src.resolvesAt != null ? Number(src.resolvesAt) : this.flightTakeoff.get(roundId);
      if (takeoffSrv != null) {
        crashAt = toPerf(takeoffSrv + Math.round(tFor(M) * 1000));
        settlesAt = crashAt == null ? null : crashAt + SETTLE_MS;
      }
    }
    const nextOpensAt = toPerf(src.nextOpensAt)
      ?? (settlesAt == null ? null : settlesAt + Number(st.table?.intermissionMs || INTERMISSION_MS));

    // ── phase ──────────────────────────────────────────────────────────────────────────
    // A round the server closed without ever flying it (the §5.2 idle short-circuit, or an
    // outage void) has no crash to animate. It holds on `locked` — the plane never leaves the
    // runway — rather than being handed a crash multiplier of 1.00 that nobody ever flew.
    const terminal = src.status === 'settled' || src.status === 'settling';
    const neverFlew = this.flightNone.has(roundId) || (terminal && M == null);

    let status: RoundStatus;
    this.syncingFlag = false;
    if (now < locksAt) {
      status = 'betting';
    } else if (now < takeoffAt) {
      status = 'locked';
    } else if (neverFlew) {
      // Not a sync problem — a documented, healthy state. The plane simply stays on the
      // runway until the next round opens, and `history()` records no crash point for it.
      status = 'locked';
    } else if (M == null || crashAt == null) {
      status = 'flying';
      this.syncingFlag = now > takeoffAt + MAX_FLIGHT_MS;
    } else if (now < crashAt) {
      status = 'flying';
    } else if (settlesAt == null || now < settlesAt) {
      status = 'crashed';
    } else {
      // Past `settles_at`: intermission until the next round opens — and PAST `nextOpensAt`
      // too, because a `pending` row is not on screen until its window actually starts.
      status = 'intermission';
    }

    const done = status === 'crashed' || status === 'intermission';

    this.displayedNo = Number(src.no);
    this.displayedId = roundId;
    this.r = {
      no: Number(src.no),
      status,
      opensAt,
      locksAt,
      takeoffAt,
      crashAt: done ? crashAt : null,
      M: done ? M : null,
      settlesAt: done ? settlesAt : null,
      nextOpensAt: done ? nextOpensAt : null,
      // §5.4: the theatre must be identical on every device at this table, so it is seeded
      // from the SERVER's table seed and round number — never a per-device localStorage seed.
      fakeSeed: mix(Number(st.table?.fakeSeed ?? this.tableSeed) >>> 0, Number(src.no)),
    };

    let panelSig = '';
    for (const p of [0, 1] as Panel[]) {
      const ps = this.panelStateFor(p);
      panelSig += `${p}${ps.kind}${(ps as { stake?: number }).stake ?? ''}`
        + `${(ps as { m?: number }).m ?? ''}${(ps as { won?: number }).won ?? ''}`
        + `${(ps as { autoX?: number | null }).autoX ?? ''}|`;
    }
    return `${src.no}|${status}|${M ?? ''}|${panelSig}|${this.displayBalance()}|${this.syncingFlag ? 's' : ''}`;
  }

  /**
   * The panel state, derived from my chips and what the server has revealed. The one
   * implementation behind both `panel()` and the change signature — the signature builder runs
   * INSIDE `recompute`, so it cannot be the method that calls `recompute`.
   */
  private panelStateFor(p: Panel): PanelState {
    const r = this.r;
    if (!r) return { kind: 'idle' };
    const mkt = marketOf(p);
    const over = r.status === 'crashed' || r.status === 'intermission';

    // Once the round on screen is finished, a fresh chip is a chip on the NEXT round and that
    // is what the panel must show — the same swap `LocalTableClock.bet` makes when it turns a
    // `lost`/`cashed` panel straight into `queued`.
    const nxt = this.bets.get(betKey(r.no + 1, mkt));
    if (over && nxt) return { kind: 'queued', stake: nxt.stake, autoX: nxt.autoX };

    const cur = this.bets.get(betKey(r.no, mkt));
    if (cur) {
      if (cur.cashed) return { kind: 'cashed', stake: cur.stake, m: cur.cashed.m, won: cur.cashed.won };
      const M = r.M;
      if (M != null && over) {
        // Rule 3: the server has revealed M, so what an AUTO order did is arithmetic, not a
        // guess — `runAutoCashouts` pays every order with `auto_cashout < M`, at exactly its X.
        if (cur.autoX != null && cur.autoX < M) {
          return { kind: 'cashed', stake: cur.stake, m: cur.autoX, won: rupeesAt(cur.stake, cur.autoX) };
        }
        return { kind: 'lost', stake: cur.stake, M };
      }
      if (r.status === 'flying') return { kind: 'flying', stake: cur.stake, autoX: cur.autoX };
      return { kind: 'placed', stake: cur.stake, autoX: cur.autoX };
    }

    if (nxt) return { kind: 'queued', stake: nxt.stake, autoX: nxt.autoX };
    return { kind: 'idle' };
  }

  private tick() {
    if (this.dead) return;
    const sig = this.recompute();
    this.maybeProbeFlight();
    // WHILE FLYING, EMIT EVERY TICK even when the signature has not moved — exactly what
    // `LocalTableClock.tick` does (`if (changed || r.status === 'flying') this.emit()`). The
    // CASH OUT face reads `mNow()`, which climbs continuously, so a signature that only tracks
    // discrete state would leave the amount on the button frozen for the whole flight.
    if (sig === this.sig && this.r?.status !== 'flying') return;
    this.sig = sig;
    this.emit();
  }

  private emit() { this.subs.forEach((cb) => { try { cb(); } catch { /* a bad subscriber must not stop the table */ } }); }
}

export default ServerTableClock;
